/**
 * useFocusAreaMutations
 *
 * React Query mutation hooks: create, update (fields), edit practice, retire.
 * Each hook invalidates ["focusAreas"] so all status-filtered list queries
 * refetch.
 * Phase 4 adds useConfirmAutoRetire and useSnoozeAutoRetire, both using
 * onSettled (not onSuccess) so stale-candidate 400 responses still refresh.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  activateFocusArea,
  confirmAutoRetire,
  createFocusArea,
  dismissFocusArea,
  editFocusAreaPractice,
  regeneratePractice,
  retireFocusArea,
  snoozeAutoRetire,
  updateFocusArea,
} from "../services/focusAreas";
import type {
  FocusArea,
  FocusAreaCreatePayload,
  FocusAreaPractice,
  FocusAreaPracticeUpdatePayload,
  FocusAreaRetirePayload,
  FocusAreaUpdatePayload,
} from "../types/focusArea";
import { FOCUS_AREAS_QUERY_KEY } from "./useFocusAreas";

export function useCreateFocusArea() {
  const qc = useQueryClient();
  return useMutation<FocusArea, Error, FocusAreaCreatePayload>({
    mutationFn: createFocusArea,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FOCUS_AREAS_QUERY_KEY });
    },
  });
}

export function useUpdateFocusArea() {
  const qc = useQueryClient();
  return useMutation<
    FocusArea,
    Error,
    { focusAreaId: string; payload: FocusAreaUpdatePayload }
  >({
    mutationFn: ({ focusAreaId, payload }) =>
      updateFocusArea(focusAreaId, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FOCUS_AREAS_QUERY_KEY });
    },
  });
}

export function useEditFocusAreaPractice() {
  const qc = useQueryClient();
  return useMutation<
    FocusAreaPractice,
    Error,
    { focusAreaId: string; payload: FocusAreaPracticeUpdatePayload }
  >({
    mutationFn: ({ focusAreaId, payload }) =>
      editFocusAreaPractice(focusAreaId, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FOCUS_AREAS_QUERY_KEY });
    },
  });
}

export function useRetireFocusArea() {
  const qc = useQueryClient();
  return useMutation<
    FocusArea,
    Error,
    { focusAreaId: string; payload: FocusAreaRetirePayload }
  >({
    mutationFn: ({ focusAreaId, payload }) =>
      retireFocusArea(focusAreaId, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FOCUS_AREAS_QUERY_KEY });
    },
  });
}

export function useActivateFocusArea() {
  const qc = useQueryClient();
  return useMutation<FocusArea, Error, string>({
    mutationFn: activateFocusArea,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FOCUS_AREAS_QUERY_KEY });
    },
  });
}

export function useDismissFocusArea() {
  const qc = useQueryClient();
  return useMutation<{ id: string; dismissed: boolean }, Error, string>({
    mutationFn: dismissFocusArea,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FOCUS_AREAS_QUERY_KEY });
    },
  });
}

export function useRegeneratePractice() {
  const qc = useQueryClient();
  return useMutation<FocusArea, Error, string>({
    mutationFn: regeneratePractice,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FOCUS_AREAS_QUERY_KEY });
    },
  });
}

export function useConfirmAutoRetire() {
  const qc = useQueryClient();
  return useMutation<FocusArea, Error, string>({
    mutationFn: confirmAutoRetire,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: FOCUS_AREAS_QUERY_KEY });
    },
  });
}

export function useSnoozeAutoRetire() {
  const qc = useQueryClient();
  return useMutation<FocusArea, Error, string>({
    mutationFn: snoozeAutoRetire,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: FOCUS_AREAS_QUERY_KEY });
    },
  });
}
